import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { meetingsAPI, chatAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  Sparkles,
  ListTodo,
  MessageSquare,
  Send,
  Loader2,
  Calendar,
  Clock,
  Search,
  CheckCircle,
  Circle,
  User,
  Languages,
  Globe,
  Users,
  ChevronDown,
  ChevronUp,
  Save,
  Link2,
  Unlink,
  X,
  Building2,
  Mail,
  Download,
  RefreshCw,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';

const LANGUAGES = {
  original: { name: 'Original', flag: '📝' },
  en: { name: 'English', flag: '🇬🇧' },
  pl: { name: 'Polski', flag: '🇵🇱' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  pt: { name: 'Português', flag: '🇵🇹' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  cs: { name: 'Čeština', flag: '🇨🇿' },
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, token } = useApp();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transcript');
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const [showReanalyzeMenu, setShowReanalyzeMenu] = useState(false);
  const reanalyzeMenuRef = useRef(null);
  const [reanalyzing, setReanalyzing] = useState(false);

  // Translation state
  const [selectedLanguage, setSelectedLanguage] = useState('original');
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);

  // Speaker mapping state
  const [speakerMapping, setSpeakerMapping] = useState({});
  const [speakerMappingDraft, setSpeakerMappingDraft] = useState({});
  const [showSpeakerPanel, setShowSpeakerPanel] = useState(false);
  const [savingSpeakers, setSavingSpeakers] = useState(false);

  // Contact linking state
  const [linkedContacts, setLinkedContacts] = useState({});
  const [linkingSpeaker, setLinkingSpeaker] = useState(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  useEffect(() => {
    fetchMeeting();
    fetchLinkedContacts();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
      if (reanalyzeMenuRef.current && !reanalyzeMenuRef.current.contains(e.target)) {
        setShowReanalyzeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMeeting = async () => {
    try {
      const data = await meetingsAPI.getById(id);
      setMeeting(data);
      // Load existing translations
      if (data.translations) {
        setTranslations(data.translations);
      }
      // Load existing speaker mapping
      if (data.speaker_mapping) {
        setSpeakerMapping(data.speaker_mapping);
        setSpeakerMappingDraft(data.speaker_mapping);
      }
    } catch (error) {
      toast.error('Failed to load meeting');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedContacts = async () => {
    try {
      const res = await fetch(`${API_BASE}/meetings/${id}/linked-contacts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLinkedContacts(data.linked_contacts || {});
      }
    } catch (error) {
      // Silent fail — linked contacts are optional
    }
  };

  const handleSearchContacts = async (query) => {
    setContactSearchQuery(query);
    if (query.length < 2) {
      setContactSearchResults([]);
      return;
    }
    setSearchingContacts(true);
    try {
      const res = await fetch(`${API_BASE}/proxy/contacts/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContactSearchResults(data);
      }
    } catch (error) {
      toast.error('Failed to search contacts');
    } finally {
      setSearchingContacts(false);
    }
  };

  const handleLinkContact = async (speakerName, contact) => {
    setLinkingInProgress(true);
    try {
      const res = await fetch(`${API_BASE}/meetings/${id}/link-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          speaker_name: speakerName,
          contact_id: contact.id,
          contact_info: {
            first_name: contact.first_name,
            last_name: contact.last_name,
            company: contact.company,
            email: contact.email,
          },
        }),
      });
      if (res.ok) {
        setLinkedContacts(prev => ({
          ...prev,
          [speakerName]: {
            contact_id: contact.id,
            contact_info: {
              first_name: contact.first_name,
              last_name: contact.last_name,
              company: contact.company,
              email: contact.email,
            },
          },
        }));
        setLinkingSpeaker(null);
        setContactSearchQuery('');
        setContactSearchResults([]);
        toast.success(`Linked ${speakerName} to ${contact.first_name} ${contact.last_name}`);
      } else {
        toast.error('Failed to link contact');
      }
    } catch (error) {
      toast.error('Failed to link contact');
    } finally {
      setLinkingInProgress(false);
    }
  };

  // Extract unique speakers from transcript
  const uniqueSpeakers = React.useMemo(() => {
    if (!meeting?.transcript) return [];
    const speakers = new Set();
    meeting.transcript.forEach(seg => {
      if (seg.speaker) speakers.add(seg.speaker);
    });
    // Also check action items for assignees
    meeting.action_items?.forEach(item => {
      if (item.assignee) speakers.add(item.assignee);
    });
    return Array.from(speakers).sort();
  }, [meeting]);

  // Helper to get display name for a speaker
  const getDisplayName = (speaker) => {
    if (!speaker) return null;
    return speakerMapping[speaker] || speaker;
  };

  // Save speaker mapping
  const handleSaveSpeakerMapping = async () => {
    setSavingSpeakers(true);
    try {
      const response = await fetch(`${API_BASE}/meetings/${id}/speakers`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ speaker_mapping: speakerMappingDraft }),
      });

      if (!response.ok) throw new Error('Failed to save');

      setSpeakerMapping(speakerMappingDraft);
      toast.success('Speaker names saved');
    } catch (error) {
      toast.error('Failed to save speaker names');
    } finally {
      setSavingSpeakers(false);
    }
  };

  const handleTranslate = async (targetLang) => {
    if (targetLang === 'original') {
      setSelectedLanguage('original');
      return;
    }

    // Check if translation already exists
    if (translations[targetLang]) {
      setSelectedLanguage(targetLang);
      return;
    }

    // Request new translation
    setTranslating(true);
    try {
      const response = await fetch(`${API_BASE}/meetings/${id}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ target_language: targetLang }),
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const data = await response.json();
      setTranslations(prev => ({ ...prev, [targetLang]: data.translation }));
      setSelectedLanguage(targetLang);
      toast.success(`Translated to ${LANGUAGES[targetLang]?.name || targetLang}`);
    } catch (error) {
      toast.error('Failed to translate');
    } finally {
      setTranslating(false);
    }
  };

  // Get current transcript, summary, and action items based on selected language
  const currentTranscript = selectedLanguage === 'original'
    ? meeting?.transcript
    : translations[selectedLanguage]?.transcript || meeting?.transcript;

  const currentSummary = selectedLanguage === 'original'
    ? meeting?.summary
    : translations[selectedLanguage]?.summary || meeting?.summary;

  const currentActionItems = selectedLanguage === 'original'
    ? meeting?.action_items
    : translations[selectedLanguage]?.action_items || meeting?.action_items;

  const handleToggleAction = async (actionId, completed) => {
    try {
      await meetingsAPI.updateActionItem(id, actionId, !completed);
      setMeeting((prev) => ({
        ...prev,
        action_items: prev.action_items.map((item) =>
          item.id === actionId ? { ...item, completed: !completed } : item
        ),
      }));
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const data = await chatAPI.send(userMessage, id);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setChatMessages((prev) => prev.slice(0, -1));
      setChatInput(userMessage);
    } finally {
      setChatLoading(false);
    }
  };

  const handleReanalyze = async (summaryDetail = 'auto', actionDetail = 'auto') => {
    setReanalyzing(true);
    setShowReanalyzeMenu(false);
    try {
      const response = await fetch(`${API_BASE}/meetings/${id}/reanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ summary_detail: summaryDetail, action_detail: actionDetail }),
      });
      if (!response.ok) throw new Error('Reanalyze failed');
      const data = await response.json();
      setMeeting(data);
      toast.success('Meeting re-analyzed successfully');
    } catch (error) {
      toast.error('Failed to re-analyze meeting');
    } finally {
      setReanalyzing(false);
    }
  };

  const exportMeeting = (includeTimestamps = true) => {
    if (!meeting) return;

    let content = `Meeting: ${meeting.title}\n`;
    content += `Date: ${format(new Date(meeting.created_at), 'PPpp')}\n`;
    if (meeting.duration) {
      content += `Duration: ${Math.floor(meeting.duration / 60)}:${(meeting.duration % 60).toString().padStart(2, '0')}\n`;
    }
    if (meeting.description) {
      content += `Description: ${meeting.description}\n`;
    }
    content += `Status: ${meeting.status}\n`;

    const transcript = currentTranscript;
    const summary = currentSummary;
    const actions = currentActionItems;
    const langLabel = selectedLanguage !== 'original'
      ? ` (${LANGUAGES[selectedLanguage]?.name})`
      : '';

    if (summary) {
      content += `\n--- Summary${langLabel} ---\n\n${summary}\n`;
    }

    if (meeting?.key_topics?.length > 0) {
      content += `\n--- Key Topics ---\n\n${meeting.key_topics.join(', ')}\n`;
    }

    if (meeting?.sentiment) {
      content += `\n--- Sentiment ---\n\n${meeting.sentiment}\n`;
    }

    if (actions?.length > 0) {
      content += `\n--- Action Items${langLabel} ---\n\n`;
      actions.forEach((item) => {
        const assignee = item.assignee ? ` [${getDisplayName(item.assignee)}]` : '';
        content += `${item.completed ? '[x]' : '[ ]'} ${item.text}${assignee}\n`;
      });
    }

    if (transcript?.length > 0) {
      content += `\n--- Transcript${langLabel}${includeTimestamps ? '' : ' (clean)'} ---\n\n`;
      transcript.forEach((seg) => {
        const speaker = seg.speaker ? `${getDisplayName(seg.speaker)}: ` : '';
        const ts = includeTimestamps && seg.timestamp ? `[${seg.timestamp}] ` : '';
        content += `${ts}${speaker}${seg.text}\n`;
      });
    }

    const suffix = includeTimestamps ? '' : '-clean';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-${meeting.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40)}-${id.substring(0, 8)}${suffix}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Meeting exported');
  };

  const filteredTranscript = currentTranscript?.filter((seg) =>
    seg.text.toLowerCase().includes(transcriptSearch.toLowerCase())
  ) || [];

  const completedActionsCount = currentActionItems?.filter((a) => a.completed).length || 0;
  const totalActionsCount = currentActionItems?.length || 0;

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-12 w-2/3 mb-4" />
          <Skeleton className="h-6 w-1/3 mb-8" />
          <Skeleton className="h-[500px] w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!meeting) return null;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('meeting.backToDashboard')}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="meeting-title">
                {meeting.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(meeting.created_at), 'PPP')}</span>
                </div>
                {meeting.duration && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{Math.floor(meeting.duration / 60)}:{(meeting.duration % 60).toString().padStart(2, '0')}</span>
                  </div>
                )}
                <Badge variant={meeting.status === 'completed' ? 'default' : 'secondary'}>
                  {meeting.status}
                </Badge>
              </div>
            </div>

            {/* Export + Translation selector */}
            {meeting.status === 'completed' && (
              <div className="flex items-center gap-3">
                <div className="relative" ref={exportMenuRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="h-9 rounded-lg"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                  {showExportMenu && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1">
                      <button
                        onClick={() => { exportMeeting(true); setShowExportMenu(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        With timestamps
                      </button>
                      <button
                        onClick={() => { exportMeeting(false); setShowExportMenu(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        Clean (no timestamps)
                      </button>
                    </div>
                  )}
                </div>
                <div className="relative" ref={reanalyzeMenuRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReanalyzeMenu(!showReanalyzeMenu)}
                    className="h-9 rounded-lg"
                    disabled={reanalyzing}
                  >
                    {reanalyzing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {reanalyzing ? 'Analyzing...' : 'Re-analyze'}
                    {!reanalyzing && <ChevronDown className="w-3 h-3 ml-1" />}
                  </Button>
                  {showReanalyzeMenu && (
                    <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1">
                      <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
                        Summary + Actions detail level
                      </div>
                      <button
                        onClick={() => handleReanalyze('concise', 'basic')}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <span className="font-medium">Concise</span>
                        <span className="block text-xs text-muted-foreground">2-3 paragraphs, key actions only</span>
                      </button>
                      <button
                        onClick={() => handleReanalyze('detailed', 'thorough')}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <span className="font-medium">Detailed</span>
                        <span className="block text-xs text-muted-foreground">4-6 paragraphs, all tasks and decisions</span>
                      </button>
                      <button
                        onClick={() => handleReanalyze('comprehensive', 'exhaustive')}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <span className="font-medium">Comprehensive</span>
                        <span className="block text-xs text-muted-foreground">8-15 paragraphs, every action/follow-up</span>
                      </button>
                      <div className="border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={() => handleReanalyze('auto', 'auto')}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-violet-600"
                        >
                          <span className="font-medium">Auto</span>
                          <span className="block text-xs text-muted-foreground">Scale based on meeting length</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-muted-foreground" />
                <Select
                  value={selectedLanguage}
                  onValueChange={handleTranslate}
                  disabled={translating}
                >
                  <SelectTrigger className="w-[180px] h-9 rounded-lg">
                    {translating ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Translating...</span>
                      </div>
                    ) : (
                      <SelectValue>
                        {LANGUAGES[selectedLanguage]?.flag} {LANGUAGES[selectedLanguage]?.name}
                      </SelectValue>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">
                      📝 Original
                    </SelectItem>
                    {Object.entries(LANGUAGES).filter(([key]) => key !== 'original').map(([code, lang]) => (
                      <SelectItem key={code} value={code}>
                        {lang.flag} {lang.name}
                        {translations[code] && ' ✓'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
              </div>
            )}
          </div>
          {meeting.description && (
            <p className="text-muted-foreground mt-3">{meeting.description}</p>
          )}
        </div>

        {/* Speaker Mapping Panel */}
        {uniqueSpeakers.length > 0 && meeting.status === 'completed' && (
          <Card className="mb-6 rounded-xl border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setShowSpeakerPanel(!showSpeakerPanel)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-violet-600" />
                <span className="font-medium">Speaker Names</span>
                <Badge variant="outline" className="ml-2">
                  {Object.keys(speakerMapping).filter(k => speakerMapping[k]).length}/{uniqueSpeakers.length} named
                </Badge>
              </div>
              {showSpeakerPanel ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {showSpeakerPanel && (
              <CardContent className="pt-0 pb-4">
                <div className="space-y-4 mb-4">
                  {uniqueSpeakers.map((speaker) => (
                    <div key={speaker} className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground w-24 flex-shrink-0">
                          {speaker}:
                        </span>
                        <Input
                          placeholder="Enter name..."
                          value={speakerMappingDraft[speaker] || ''}
                          onChange={(e) => setSpeakerMappingDraft(prev => ({
                            ...prev,
                            [speaker]: e.target.value
                          }))}
                          className="h-9 rounded-lg flex-1"
                        />
                        {linkedContacts[speaker] ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 text-green-600 hover:text-red-600 flex-shrink-0"
                            title="Unlink contact"
                            onClick={() => {
                              setLinkedContacts(prev => {
                                const next = { ...prev };
                                delete next[speaker];
                                return next;
                              });
                            }}
                          >
                            <Unlink className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 flex-shrink-0"
                            onClick={() => {
                              setLinkingSpeaker(speaker);
                              const name = speakerMappingDraft[speaker] || speaker;
                              handleSearchContacts(name);
                            }}
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Link
                          </Button>
                        )}
                      </div>
                      {/* Linked contact info */}
                      {linkedContacts[speaker] && (
                        <div className="ml-[calc(6rem+0.75rem)] flex items-center gap-2 text-xs text-muted-foreground bg-green-50 dark:bg-green-900/20 px-3 py-1.5 rounded-lg">
                          <Link2 className="w-3 h-3 text-green-600" />
                          <span className="font-medium text-green-700 dark:text-green-400">
                            {linkedContacts[speaker].contact_info?.first_name} {linkedContacts[speaker].contact_info?.last_name}
                          </span>
                          {linkedContacts[speaker].contact_info?.company && (
                            <>
                              <Building2 className="w-3 h-3 ml-1" />
                              <span>{linkedContacts[speaker].contact_info.company}</span>
                            </>
                          )}
                          {linkedContacts[speaker].contact_info?.email && (
                            <>
                              <Mail className="w-3 h-3 ml-1" />
                              <span>{linkedContacts[speaker].contact_info.email}</span>
                            </>
                          )}
                        </div>
                      )}
                      {/* Contact search dropdown */}
                      {linkingSpeaker === speaker && (
                        <div className="ml-[calc(6rem+0.75rem)] border rounded-lg bg-background shadow-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Search className="w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search ExpoPulse contacts..."
                              value={contactSearchQuery}
                              onChange={(e) => handleSearchContacts(e.target.value)}
                              className="h-8 text-sm flex-1"
                              autoFocus
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => {
                                setLinkingSpeaker(null);
                                setContactSearchQuery('');
                                setContactSearchResults([]);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="max-h-40 overflow-y-auto">
                            {searchingContacts && (
                              <div className="flex items-center justify-center py-3">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                <span className="text-sm text-muted-foreground">Searching...</span>
                              </div>
                            )}
                            {!searchingContacts && contactSearchResults.length === 0 && contactSearchQuery.length >= 2 && (
                              <p className="text-sm text-muted-foreground text-center py-3">No contacts found</p>
                            )}
                            {!searchingContacts && contactSearchQuery.length < 2 && (
                              <p className="text-sm text-muted-foreground text-center py-3">Type at least 2 characters to search</p>
                            )}
                            {contactSearchResults.map((contact) => (
                              <button
                                key={contact.id}
                                onClick={() => handleLinkContact(speaker, contact)}
                                disabled={linkingInProgress}
                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3"
                              >
                                <User className="w-4 h-4 text-violet-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {contact.first_name} {contact.last_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {[contact.company, contact.email].filter(Boolean).join(' · ')}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveSpeakerMapping}
                    disabled={savingSpeakers}
                    className="rounded-lg bg-violet-600 hover:bg-violet-700"
                    size="sm"
                  >
                    {savingSpeakers ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Names
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl bg-muted/50 p-1" data-testid="meeting-tabs">
            <TabsTrigger value="transcript" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm" data-testid="tab-transcript">
              <FileText className="w-4 h-4 mr-2" />
              {t('meeting.tabs.transcript')}
            </TabsTrigger>
            <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm" data-testid="tab-summary">
              <Sparkles className="w-4 h-4 mr-2" />
              {t('meeting.tabs.summary')}
            </TabsTrigger>
            <TabsTrigger value="actions" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm" data-testid="tab-actions">
              <ListTodo className="w-4 h-4 mr-2" />
              {t('meeting.tabs.actions')}
            </TabsTrigger>
            <TabsTrigger value="chat" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm" data-testid="tab-chat">
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('meeting.tabs.chat')}
            </TabsTrigger>
          </TabsList>

          {/* Transcript Tab */}
          <TabsContent value="transcript">
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>{t('meeting.transcript.title')}</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder={t('meeting.transcript.searchPlaceholder')}
                      value={transcriptSearch}
                      onChange={(e) => setTranscriptSearch(e.target.value)}
                      className="pl-9 h-9 rounded-lg"
                      data-testid="transcript-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {selectedLanguage !== 'original' && (
                  <Badge variant="outline" className="mb-4">
                    <Globe className="w-3 h-3 mr-1" />
                    {LANGUAGES[selectedLanguage]?.name} translation
                  </Badge>
                )}
                {filteredTranscript.length > 0 ? (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-4">
                      {filteredTranscript.map((segment, index) => (
                        <div
                          key={index}
                          className="flex gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
                          data-testid={`transcript-segment-${index}`}
                        >
                          <div className="flex-shrink-0">
                            <span className="text-xs font-mono text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30 px-2 py-1 rounded">
                              {segment.timestamp}
                            </span>
                          </div>
                          <div className="flex-1">
                            {segment.speaker && (
                              <span className="text-sm font-medium text-muted-foreground block mb-1">
                                {getDisplayName(segment.speaker)}
                                {speakerMapping[segment.speaker] && (
                                  <span className="text-xs text-slate-400 ml-2">({segment.speaker})</span>
                                )}
                              </span>
                            )}
                            <p className="text-foreground leading-relaxed">{segment.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('meeting.transcript.noTranscript')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary">
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle>{t('meeting.summary.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                {currentSummary ? (
                  <div className="space-y-6">
                    <div className="prose prose-slate dark:prose-invert max-w-none" data-testid="meeting-summary">
                      {selectedLanguage !== 'original' && (
                        <Badge variant="outline" className="mb-4">
                          <Globe className="w-3 h-3 mr-1" />
                          {LANGUAGES[selectedLanguage]?.name} translation
                        </Badge>
                      )}
                      {currentSummary.split('\n').map((paragraph, index) => (
                        <p key={index} className="leading-relaxed text-foreground">
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {/* Key Topics */}
                    {meeting?.key_topics?.length > 0 && (
                      <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                          {t('meeting.summary.keyTopics')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {meeting.key_topics.map((topic, i) => (
                            <Badge key={i} variant="secondary" className="text-sm">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sentiment */}
                    {meeting?.sentiment && (
                      <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                          {t('meeting.summary.sentiment')}
                        </h4>
                        <p className="text-foreground leading-relaxed">{meeting.sentiment}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('meeting.summary.noSummary')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Items Tab */}
          <TabsContent value="actions">
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('meeting.actions.title')}</CardTitle>
                  {totalActionsCount > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {completedActionsCount}/{totalActionsCount} {t('meeting.actions.completed')}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedLanguage !== 'original' && currentActionItems?.length > 0 && (
                  <Badge variant="outline" className="mb-4">
                    <Globe className="w-3 h-3 mr-1" />
                    {LANGUAGES[selectedLanguage]?.name} translation
                  </Badge>
                )}
                {currentActionItems?.length > 0 ? (
                  <div className="space-y-3" data-testid="action-items-list">
                    {currentActionItems.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                          item.completed
                            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-violet-300 dark:hover:border-violet-800'
                        }`}
                        data-testid={`action-item-${item.id}`}
                      >
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() => handleToggleAction(item.id, item.completed)}
                          className="mt-1"
                          data-testid={`action-checkbox-${item.id}`}
                        />
                        <div className="flex-1">
                          <p className={`${item.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {item.text}
                          </p>
                          {item.assignee && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                              <User className="w-3 h-3" />
                              {getDisplayName(item.assignee)}
                              {speakerMapping[item.assignee] && (
                                <span className="text-xs text-slate-400 ml-1">({item.assignee})</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('meeting.actions.noActions')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle>{t('meeting.chat.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col h-[500px]">
                  {/* Messages */}
                  <ScrollArea className="flex-1 pr-4">
                    <div className="space-y-4">
                      {chatMessages.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Ask anything about this meeting...</p>
                        </div>
                      )}
                      {chatMessages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          data-testid={`chat-message-${index}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                              msg.role === 'user'
                                ? 'bg-violet-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-foreground'
                            }`}
                          >
                            <p className="leading-relaxed">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex gap-3 justify-start">
                          <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <form onSubmit={handleSendChat} className="flex gap-2 pt-4 mt-4 border-t border-slate-200 dark:border-slate-800">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={t('meeting.chat.placeholder')}
                      className="flex-1 h-11 rounded-xl"
                      disabled={chatLoading}
                      data-testid="chat-input"
                    />
                    <Button
                      type="submit"
                      className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700"
                      disabled={chatLoading || !chatInput.trim()}
                      data-testid="chat-send-button"
                    >
                      {chatLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MeetingDetail;
